export class ConversationsView {
	constructor(state, options = {}) {
		this.state = state;
		this.options = options;
		this.element = null;
		this._unsubscribe = null;
		this.avatarColors = [
			'#155EEF',
			'#8b5cf6',
			'#10b981',
			'#f59e0b',
			'#ef4444',
			'#ec4899',
			'#06b6d4',
		];
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = 'liveChat-view liveChat-conversations-view';

		this._updateContent();
		this._attachEvents();

		this._unsubscribe = this.state.subscribe((type) => {
			if (
				type === 'conversationsUpdate' ||
				type === 'conversationAdded' ||
				type === 'conversationRead' ||
				type === 'conversationUpdated'
			) {
				this._updateContent();
			}
		});

		return this.element;
	}

	_getAvatarColor(name) {
		// Generate consistent color based on name
		const charCode = (name || 'S').charCodeAt(0);
		return this.avatarColors[charCode % this.avatarColors.length];
	}

	_updateContent() {
		const conversations = this.state.conversations;

		let conversationsHtml;
		if (conversations.length === 0) {
			conversationsHtml = `
            <div class="liveChat-empty-state">
                <div class="liveChat-empty-state-icon">
                    <iconify-icon icon="ph:chat-circle" width="48" height="48"></iconify-icon>
                </div>
                <h3>No conversations yet</h3>
                <p>Start a new conversation with our team</p>
            </div>
        `;
		} else {
			conversationsHtml = `
            <div class="liveChat-conversations-list">
                ${conversations.map((conv) => this._renderConversationItem(conv)).join('')}
            </div>
        `;
		}

		this.element.innerHTML = `
        <div class="liveChat-conversations-header">
            <h2>Messages</h2>
           <button class="sdk-close-btn liveChat-mobile-close-btn" aria-label="Close">
  <iconify-icon icon="ph:x" width="18" height="18"></iconify-icon>
</button>
        </div>

        <div class="liveChat-conversations-body">
            ${conversationsHtml}
        </div>

        <div class="liveChat-conversations-footer">
            <button class="liveChat-new-message-btn">
                <iconify-icon icon="ph:pencil-simple" width="16" height="16" style="flex-shrink: 0; color: var(--msg-text-secondary);"></iconify-icon>
                <span style="flex: 1;">New conversation</span>
                <iconify-icon icon="ph:caret-right" width="16" height="16" style="flex-shrink: 0; color: var(--msg-text-tertiary);"></iconify-icon>
            </button>
        </div>
    `;

		this._attachEvents();
	}

	_renderConversationItem(conversation) {
		const isClosed = conversation.status === 'closed';
		const unreadClass = conversation.unread > 0 ? 'unread' : '';
		const closedClass = isClosed ? 'closed' : '';
		const timeAgo = this._formatTimeAgo(conversation.lastMessageTime);
		const avatarsHtml = this._renderConversationAvatars(
			conversation.participants
		);

		return `
			<div class="liveChat-conversation-item ${unreadClass} ${closedClass}" role="button" tabindex="0" data-conversation-id="${conversation.id}">
				<div class="liveChat-conversation-avatars">
					${avatarsHtml}
				</div>
				<div class="liveChat-conversation-content">
					<div class="liveChat-conversation-header">
						<span class="liveChat-conversation-title">${conversation.title || 'Chat with team'}</span>
						<span class="liveChat-conversation-time">${timeAgo}</span>
					</div>
					<div class="liveChat-conversation-preview">
						${conversation.unread > 0 ? '<span class="liveChat-unread-dot"></span>' : ''}
						${isClosed ? '<span class="liveChat-conversation-resolved-badge">Resolved</span>' : ''}
						<span class="liveChat-conversation-message">${this._truncateMessage(conversation.lastMessage)}</span>
					</div>
				</div>
			</div>
		`;
	}

	_renderConversationAvatars(participants) {
		if (!participants || participants.length === 0) {
			const color = this._getAvatarColor('S');
			return `<div class="sdk-avatar sdk-avatar-md" style="background-color: ${color};">S</div>`;
		}

		const p = participants[0];
		if (p.avatarUrl) {
			return `<div class="sdk-avatar sdk-avatar-md"><img src="${p.avatarUrl}" alt="${p.name}" /></div>`;
		}
		const initial = (p.name || 'S').charAt(0).toUpperCase();
		const color = this._getAvatarColor(p.name);
		return `<div class="sdk-avatar sdk-avatar-md" style="background-color: ${color};">${initial}</div>`;
	}

	_renderAvatarStack() {
		const avatars = this.state.teamAvatars;
		if (!avatars || avatars.length === 0) {
			const color1 = this._getAvatarColor('S');
			const color2 = this._getAvatarColor('T');
			return `
				<div class="liveChat-avatar-stack">
					<div class="sdk-avatar sdk-avatar-sm" style="background-color: ${color1};">S</div>
					<div class="sdk-avatar sdk-avatar-sm" style="background-color: ${color2};">T</div>
				</div>
			`;
		}

		const avatarItems = avatars
			.slice(0, 2)
			.map((avatar) => {
				if (typeof avatar === 'string' && avatar.startsWith('http')) {
					return `<div class="sdk-avatar sdk-avatar-sm"><img src="${avatar}" alt="Team member" /></div>`;
				}
				const initial = avatar.charAt(0).toUpperCase();
				const color = this._getAvatarColor(avatar);
				return `<div class="sdk-avatar sdk-avatar-sm" style="background-color: ${color};">${initial}</div>`;
			})
			.join('');

		return `<div class="liveChat-avatar-stack">${avatarItems}</div>`;
	}

	_formatTimeAgo(timestamp) {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		const now = new Date();
		const diffMs = now - date;
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return 'now';
		if (diffMins < 60) return `${diffMins}m`;
		if (diffHours < 24) return `${diffHours}h`;
		if (diffDays < 7) return `${diffDays}d`;
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	_truncateMessage(message) {
		if (!message) return 'No messages yet';
		const maxLength = 50;
		if (message.length <= maxLength) return message;
		return message.substring(0, maxLength) + '...';
	}

	_attachEvents() {
		const closeBtn = this.element.querySelector('.sdk-close-btn');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => {
				this.state.setOpen(false);
			});
		}

		this.element
			.querySelectorAll('.liveChat-conversation-item')
			.forEach((item) => {
				item.addEventListener('click', () => {
					const convId = item.dataset.conversationId;
					this.state.setActiveConversation(convId);
					this.state.markAsRead(convId);
					this.state.setView('chat');

					if (this.options.onSelectConversation) {
						this.options.onSelectConversation(convId);
					}
				});
			});

		const newMsgBtn = this.element.querySelector('.liveChat-new-message-btn');
		if (newMsgBtn) {
			newMsgBtn.addEventListener('click', () => {
				this._startNewConversation();
			});
		}
	}

	_startNewConversation() {
		// If there's an open conversation, route to it instead of creating new
		const openConversation = this.state.conversations.find(
			(c) => c.status === 'open'
		);

		if (openConversation) {
			this.state.setActiveConversation(openConversation.id);
			this.state.markAsRead(openConversation.id);
			this.state.setView('chat');
			if (this.options.onSelectConversation) {
				this.options.onSelectConversation(openConversation.id);
			}
		} else {
			this.state.setActiveConversation(null);
			if (this.options.onStartNewConversation) {
				this.options.onStartNewConversation();
			} else {
				this.state.setView('chat');
			}
		}
	}

	destroy() {
		if (this._unsubscribe) {
			this._unsubscribe();
		}
		if (this.element && this.element.parentNode) {
			this.element.parentNode.removeChild(this.element);
		}
	}
}
